import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useFileUploadFiles, useFileUploadStore } from '../store'

/**
 * Hook to manage navigation blocking when on upload page with validated files
 *
 * @returns Object with shouldBlockNavigation flag and handleNavigationAttempt function
 */
export const useNavigationBlock = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const files = useFileUploadFiles()
  const { isUploading } = useFileUploadStore()

  // Check if we're currently on an upload page
  const isOnUploadPage = location.pathname.includes('/upload')

  // Check if there are any files added (including failed/cancelled)
  const hasFiles = useCallback(() => {
    return Object.keys(files).length > 0
  }, [files])

  // Check if navigation should be blocked
  const shouldBlockNavigation = isOnUploadPage && hasFiles() && !isUploading

  /**
   * Handle navigation attempt - returns true if navigation should proceed
   * Returns false if navigation was blocked and dialog should be shown
   */
  const handleNavigationAttempt = useCallback(
    (targetPath: string): boolean => {
      // If not on upload page, allow navigation
      if (!isOnUploadPage) {
        return true
      }

      // If navigating to another upload route, allow it
      if (targetPath.includes('/upload')) {
        return true
      }

      // If actively uploading, allow navigation (floating indicator will show progress)
      if (isUploading) {
        navigate(targetPath)
        return true
      }

      // If there are any files, block navigation
      if (hasFiles()) {
        return false
      }

      // No files, allow navigation
      return true
    },
    [isOnUploadPage, isUploading, hasFiles, navigate]
  )

  return {
    shouldBlockNavigation,
    handleNavigationAttempt,
    isOnUploadPage,
  }
}
